import React, { useState, useContext, useEffect } from "react";
import QRCode from "react-qr-code";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { inpS, btnP, btnSec, crd, bdg } from "../../constants/styles";
import { gradCardStyle, gradLift, glassPill } from "../../constants/gradCard";
import MC from "../common/MC";
import { formatPropAddr } from "../../utils/propertyAddr";
import Ic from "../common/Ic";
import Fld from "../common/Fld";
import CopyBtn from "../common/CopyBtn";
import { getBankRows } from "../../utils/bankDetails";
import { applyAdvanceToDue } from "../../utils/advance";
import { getLeaseForTenant, getLeaseStatus, getDaysRemaining, isExpiringSoon, leaseStatusColor, leaseStatusLabel, isOverdue } from "../../utils/leaseHelpers";
import { getPropertyFit } from "../../utils/propertyFit";
import { monthSortValue, rentPeriod } from "../../utils/dateFormat";
import FitBadge from "../common/FitBadge";
import ReceiptView from "../landlord/ReceiptView";
import { RatingSummary, WriteReviewModal } from "../common/Reviews";

function TenDash() {
  const { t, h, props, tens, setTens, pays, setPays, mts, ccs, msgs, go, currUserId, setCurrUserId, flash, addNotif, settings, showConfirm, leases, role, units, orgId, setOrgId, orgName, refreshData } = useContext(Ctx); const ct = currUserId; const me = tens.find(x => x.id === ct);
  // Read-only "former tenant" mode: all this tenant's tenancies have been
  // settled, but the server still issued a (read-only) session so they can
  // view their own history. The flag is set at login (api.login → localStorage
  // 'formerTenant'). It drives the early-return read-only view below AND
  // suppresses the 0-active-orgs force-logout in the org-sync effect.
  const isFormer = (() => { try { return localStorage.getItem("formerTenant") === "true"; } catch (_) { return false; } })();

  // Active vacate request from the new state-machine flow. Surfaced
  // here as a top-of-dashboard banner so a tenant who's submitted
  // their move-out record gets immediate confirmation (and a
  // 1-tap path back to review what they sent).
  //
  // The vacate flow is separate from the legacy `me.vac` flag —
  // a tenant going through VacatePage creates a `vacate_requests`
  // row without flipping `me.vac`, so the dashboard wouldn't show
  // anything unless we fetch it explicitly here.
  const [activeVacate, setActiveVacate] = useState(null);
  // Reviews: this tenant's landlord reputation + whether they may rate them.
  const [landlordRep, setLandlordRep] = useState(null);
  const [rateLandlordOpen, setRateLandlordOpen] = useState(false);
  const [canRateLandlord, setCanRateLandlord] = useState(false);
  useEffect(() => {
    if (!orgId) return;
    let alive = true;
    api.getLandlordReviews(orgId).then(d => { if (alive && d && d.success) setLandlordRep(d); }).catch(() => {});
    api.getEligibleReviews().then(d => { if (alive && d && d.success) setCanRateLandlord((d.items || []).some(x => x.type === "landlord")); }).catch(() => {});
    return () => { alive = false; };
  }, [orgId]);
  useEffect(() => {
    let cancelled = false;
    api.vacateListMine().then(res => {
      if (cancelled || !res?.success) return;
      // Pick the most-recent non-closed/non-cancelled request.
      // Statuses: initiated, under_review, full_refund_proposed,
      // deduction_proposed, settled, closed, cancelled.
      const open = (res.vacate_requests || [])
        .filter(v => !["closed", "cancelled", "settled"].includes(v.status))
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
      setActiveVacate(open || null);
    }).catch(() => { /* swallow — banner just stays hidden */ });
    return () => { cancelled = true; };
  }, []);
  // Multi-unit: get all units assigned to this tenant
  const myUnits = (units || []).filter(u => u.tid === ct);
  const myProp = props.find(x => x.id === me?.pid) || (myUnits.length > 0 ? props.find(x => x.id === myUnits[0].property_id) : null);
  const myLease = getLeaseForTenant(leases, ct); const myLeaseStatus = myLease ? getLeaseStatus(myLease) : null; const myLeaseDays = myLease ? getDaysRemaining(myLease) : null;
  // Settlement status of the 🔧 payment row behind an applied charge.
  // Listing the charge bare made an already-paid one (e.g. auto-covered
  // from advance credit) look like an outstanding due that mysteriously
  // wasn't counted in the Common total.
  const maintChargeSt = (mc) => {
    const row = pays.find(p => p.tid === ct && typeof p.id === "string" && p.id.startsWith(`pay_maint_${mc.id}_`));
    if (!row) return null;
    return row.st === "paid" ? "paid" : row.pending ? "pending" : "due";
  };
  const maintChargePill = (mc) => {
    const st = maintChargeSt(mc);
    if (!st) return null;
    const c = st === "paid" ? "#16a34a" : st === "pending" ? (h.wn || "#d97706") : (h.rd || "#ef4444");
    return <span style={{ marginLeft: 6, padding: "1px 8px", borderRadius: 999, fontSize: 9, fontWeight: 700, background: `${c}15`, color: c }}>{st === "paid" ? "✓ Paid" : st === "pending" ? "⏳ Pending" : "Due"}</span>;
  };
  // Only list charges that still affect money owed — settled ones are
  // already visible under My Payments, so repeating them here read as a
  // phantom outstanding amount.
  const myMaintCharges = mts.filter(m => m.exp_amt > 0 && m.exp_applied && m.exp_target === ct)
    .filter(mc => maintChargeSt(mc) !== "paid");
  const myPay = pays.filter(x => x.tid === ct); const duePay = myPay.find(x => (x.st === "due" || x.st === "partial") && !(x.mo || "").startsWith("🔧")); const recentPay = myPay.filter(x => x.st === "paid").slice(0, 3);
  // Calculate common charge share — skip if property has no_common_charges
  // PostgreSQL boolean can come as true/false, "true"/"false", 1/0, "t"/"f"
  const rawNoCommon = myProp?.no_common_charges;
  const propNoCommon = rawNoCommon === true || rawNoCommon === "true" || rawNoCommon === "t" || rawNoCommon === 1 || rawNoCommon === "1";
  const myShare = (!propNoCommon && ccs.length > 0) ? ccs.reduce((total, cc) => {
    if (!cc.n || cc.n <= 0) return total;
    const ccTotal = (cc.items && cc.items.length > 0)
      ? cc.items.reduce((s, it) => s + Number(it.amount || 0), 0)
      : ((cc.eb || 0) + (cc.cl || 0));
    return total + Math.round(ccTotal / cc.n);
  }, 0
  ) : 0;

  // Org switcher — fetch all tenant orgs.
  //
  // Also handles "zombie session" recovery: if the landlord settled this
  // tenant's vacate, their tenants row gets `detached_at` stamped and
  // `/my-orgs` no longer returns this org. But the tenant's JWT still
  // points at the now-dead org — rendering against that context produces
  // an empty / broken-looking dashboard (no property, no payments, no
  // lease). Catch that here:
  //   - active orgs > 0 but current orgId not in list → auto-switch to
  //     the first active tenancy so the dashboard re-renders correctly.
  //   - active orgs == 0 → no active tenancies anywhere, force logout so
  //     the user picks up the "all your tenancies have ended" message on
  //     the next sign-in attempt instead of staring at a blank screen.
  const [myOrgs, setMyOrgs] = useState([]);
  const [showOrgPicker, setShowOrgPicker] = useState(false);
  const [switchingOrg, setSwitchingOrg] = useState(null);
  // Former-tenant portal: the tenant's settled-out tenancies (from /my-orgs
  // `ended`), so someone who vacated multiple properties can switch between
  // each past tenancy's records.
  const [pastTenancies, setPastTenancies] = useState([]);
  const [switchingPast, setSwitchingPast] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.getMyOrgs().then(async r => {
      if (cancelled || !r?.success) return;
      const orgs = r.orgs || [];
      setPastTenancies(r.ended || []);
      const stillBelong = orgs.some(o => o.org_id === orgId);
      if (orgs.length === 0) {
        // No ACTIVE tenancies. A FORMER tenant (read-only session) is
        // EXPECTED to have zero active orgs — keep them signed in so the
        // read-only history view below can render. Only force-logout a tenant
        // whose tenancy ended mid-session (i.e. no former-tenant marker).
        if (isFormer) return;
        // All tenancies settled — nothing to render against.
        flash("Your tenancy has ended. Please sign in again.", "error");
        setTimeout(() => {
          // Clear the REAL session keys (matches api.js logout). The old
          // code removed 'token'/'user' which don't exist — the token lives
          // under 'auth_token' — so it survived the reload, the app
          // re-authenticated, landed back here with 0 orgs, and redirected
          // again: an infinite "Authenticating…" reload loop. Clearing the
          // actual keys lets the redirect drop to the login screen cleanly.
          try {
            ['auth_token', 'loggedIn', 'role', 'currUserId', 'orgId', 'maint_assignees', 'token', 'user']
              .forEach(k => localStorage.removeItem(k));
          } catch (_) {}
          window.location.href = '/';
        }, 1800);
        return;
      }
      // Active tenancy exists. If a stale former-tenant marker is still set
      // (e.g. the landlord re-assigned this tenant a property after move-out),
      // clear it and reload into the normal active dashboard.
      if (isFormer) { try { localStorage.removeItem("formerTenant"); } catch (_) {} window.location.reload(); return; }
      if (!stillBelong) {
        // Current org context is now dead — switch to the first
        // remaining active tenancy so the user sees real data again.
        const target = orgs[0];
        try {
          const sw = await api.switchOrg(target.org_id);
          if (cancelled) return;
          if (sw.success) {
            if (sw.user?.id) setCurrUserId(sw.user.id);
            if (sw.orgId) setOrgId(sw.orgId);
            flash(`Your previous tenancy ended — switched to ${target.org_name}`);
            await refreshData();
            return; // effect will rerun once orgId changes
          }
        } catch (_) { /* fall through to picker if switch failed */ }
      }
      if (orgs.length > 1) setMyOrgs(orgs);
    }).catch(() => { /* swallow — switcher just stays hidden */ });
    return () => { cancelled = true; };
  }, [orgId]);
  const handleSwitchOrg = async (org) => {
    if (org.org_id === orgId) { setShowOrgPicker(false); return; }
    setSwitchingOrg(org.org_id);
    const res = await api.switchOrg(org.org_id);
    if (res.success) {
      setCurrUserId(res.user.id);
      if (res.orgId) setOrgId(res.orgId);
      flash(`Switched to ${org.org_name}`);
      setShowOrgPicker(false);
      await refreshData();
    } else flash(res.message || "Switch failed", "error");
    setSwitchingOrg(null);
  };

  // Tenant association requests
  const [pendingRequests, setPendingRequests] = useState([]);
  const [respondingId, setRespondingId] = useState(null);
  useEffect(() => { api.getMyRequests().then(r => { if (r.success && r.requests?.length > 0) setPendingRequests(r.requests); }); }, []);
  const handleRespond = async (reqId, action) => {
    setRespondingId(reqId);
    const res = await api.respondToRequest(reqId, action);
    if (res.success) {
      setPendingRequests(prev => prev.filter(r => r.id !== reqId));
      flash(action === "approve" ? "✅ You've been linked to the property!" : "Request declined");
      if (action === "approve") setTimeout(() => window.location.reload(), 1500);
    } else flash(res.message || "Failed", "error");
    setRespondingId(null);
  };

  const requestVacate = () => {
    showConfirm(t.vacConfirm, () => {
      api.requestVacate(ct);
      setTens(tens.map(t => t.id === ct ? { ...t, vac: true } : t));
      addNotif("landlord", null, `${me.name} requested to vacate ${formatPropAddr(myProp)}.`, "info");
      flash(t.vacRequested);
    });
  };
  const grossDue = duePay ? (duePay.amt + (duePay.com || 0)) : 0;
  const alreadyPaid = duePay ? (duePay.txns || []).reduce((s, tx) => s + (tx.status === "verified" ? tx.amt : 0), 0) : 0;
  // Subtract any pending (tenant-submitted, awaiting landlord approval) from
  // displayed outstanding — the tenant has paid; the system just hasn't
  // confirmed yet. Without this they see "still owed" amounts on money they
  // already submitted, which feels broken.
  const rentPending = duePay?.pending?.amt || 0;
  const totalDue = Math.max(0, grossDue - alreadyPaid - rentPending);
  // Per-tenant maintenance charges live as separate "🔧"-prefixed payment rows.
  // Hoisted so the Total tile, the Pay Now button, and the IIFE below all stay
  // in sync. Each row's outstanding = gross minus any pending submission.
  const maintRowsDue = myPay.filter(x => (x.st === "due" || x.st === "partial") && (x.mo || "").startsWith("🔧"));
  const maintComTotal = maintRowsDue.reduce((s, p) => {
    const gross = (p.amt || 0) + (p.com || 0);
    const pendAmt = p.pending?.amt || 0;
    return s + Math.max(0, gross - pendAmt);
  }, 0);
  // Show "X awaiting approval" so the tenant knows their submitted money is
  // tracked even though it's been deducted from the outstanding total.
  const awaitingApproval = rentPending + maintRowsDue.reduce((s, p) => s + (p.pending?.amt || 0), 0);
  const grandDue = totalDue + maintComTotal;

  // ── Tenant advance credit auto-application ───────────────────────────
  // If the tenant has any unapplied advance balance (from past extra
  // payments — `payment.advance` field), net it against what's currently
  // due so the dashboard shows the *real* amount they need to pay.
  // appliedAdvance is the smaller of (available, due) — we never apply
  // more than the outstanding amount. The actual data write to consume
  // the advance happens inside `materializeAdvance` when the tenant taps
  // Pay Now (idempotent — only writes when there's something to apply).
  const availableAdvance = myPay.reduce((s, p) => s + (p.advance || 0), 0);
  const appliedAdvance = duePay ? Math.min(availableAdvance, grandDue) : 0;
  const netGrandDue = Math.max(0, grandDue - appliedAdvance);
  const [showPay, setShowPay] = useState(false); const [payAmt, setPayAmt] = useState(""); const [payMth, setPayMth] = useState("upi");
  // Light-touch collapse for the property "Match" block. Most tenants
  // glance at this card daily for rent / lease / map; the orange match
  // explainer is interesting once, not every visit. Default collapsed
  // to a single line with the score chip; tap to reveal full breakdown.
  const [showFit, setShowFit] = useState(false);
  const pendingApproval = duePay?.pending;
  const [showCommonSplit, setShowCommonSplit] = useState(false);
  const [upiOpened, setUpiOpened] = useState(false);
  const [receiptPay, setReceiptPay] = useState(null);
  const dueIsOverdue = duePay ? isOverdue(duePay.mo) : false;
  const myOpenMaint = mts.filter(m => m.tid === ct && (m.st === "open" || m.st === "assigned"));
  const hasDraftLease = myLease && (myLease.status === "draft" || getLeaseStatus(myLease) === "draft");
  const hasAttention = dueIsOverdue || pendingApproval || hasDraftLease || (myLease && isExpiringSoon(myLease)) || myOpenMaint.length > 0;

  // Materialize the advance application: decrement source records and add
  // a verified "advance-applied" txn to the current rent row. Idempotent —
  // if there's nothing to apply, does nothing. Called from Pay Now tap so
  // TenPayNow renders against the already-reduced rent row (single source
  // of truth — no two-screen disagreement).
  const materializeAdvance = async () => {
    if (appliedAdvance <= 0 || !duePay) return;
    // Reshape to the NET remaining due (same workaround as the landlord's
    // PayPage) — applyAdvanceToDue works off amt+com gross, so passing the
    // live row consumed advance against amounts already covered by
    // verified txns, silently destroying the excess credit.
    const target = { ...duePay, amt: 0, com: totalDue, txns: duePay.txns || [] };
    const { newPay: finalPay, updatedPays, consumed } = applyAdvanceToDue(target, pays, ct);
    if (consumed <= 0) return;
    const advTxn = finalPay.txns[finalPay.txns.length - 1];
    // Keep the original amt/com split but copy the status upgrade —
    // dropping finalPay.st left a permanent "due ₹0" zombie card when
    // the advance fully covered the rent.
    const merged = { ...duePay, st: finalPay.st, dt: finalPay.dt || duePay.dt, txns: [...(duePay.txns || []), advTxn] };
    setPays(prev => {
      const withSourcesReduced = prev.map(x => updatedPays.find(u => u.id === x.id) || x);
      return withSourcesReduced.map(x => x.id === merged.id ? merged : x);
    });
    try { await api.savePayment(merged); } catch (_) {}
    await Promise.all(updatedPays.map(u => api.savePayment(u).catch(() => {})));
    const advLeft = Math.max(0, grandDue - consumed);
    addNotif("tenant", ct, advLeft <= 0
      ? `✅ ₹${consumed.toLocaleString()} from your advance credit was applied to your ${duePay.mo} rent. ${duePay.mo} is now fully paid.`
      : `💰 ₹${consumed.toLocaleString()} from your advance credit was applied to your ${duePay.mo} rent. ₹${advLeft.toLocaleString()} is still due for ${duePay.mo}.`,
      "payment");
  };

  const handlePay = () => {
    const amt = Number(payAmt); if (!amt || amt <= 0) return;
    if (amt > totalDue) { flash(t.amtExceeds, "error"); return; }
    const txn = { id: `tx${Date.now()}`, tid: ct, amt, mth: payMth, dt: new Date().toISOString().slice(0, 10), type: amt >= totalDue ? "full" : "partial", status: "pending", mo: duePay.mo, totalWas: totalDue };
    const updated = { ...duePay, pending: { amt, mth: payMth, dt: new Date().toISOString().slice(0, 10), status: "waiting" }, txns: [...(duePay.txns || []), txn] };
    setPays(pays.map(x => x.id === duePay.id ? updated : x));
    api.savePayment(updated);
    addNotif("landlord", null, `${me?.name} submitted ₹${amt.toLocaleString()} payment (${payMth.toUpperCase()}) — awaiting approval`, "payment");
    flash(t.paySubmitted);
    setShowPay(false); setPayAmt("");
  };

  // ── Former-tenant read-only view ──────────────────────────────────
  // All tenancies settled (detached). Render a history-only dashboard:
  // past property, payment history, signed agreement (with move-in/out
  // photos), an optional landlord rating, and a map link to find a new
  // home. No Pay-Now / vacate / maintenance actions — and every write
  // path is blocked server-side for a detached tenant anyway.
  if (isFormer) {
    const pastLease = myLease || (leases || []).find(l => l.tenant_id === ct) || null;
    const pastProp = myProp || (pastLease ? props.find(p => p.id === pastLease.property_id) : null);
    const paidRows = (pays || []).filter(p => p.st === "paid");
    const paidCount = paidRows.length;
    const totalPaid = paidRows.reduce((s, p) => s + ((p.amt || 0) + (p.com || 0)), 0);
    const switchPast = async (tid) => {
      if (tid === ct || switchingPast) return;
      setSwitchingPast(tid);
      const res = await api.formerSwitch(tid);
      if (res && res.success) { window.location.reload(); }
      else { flash((res && res.message) || "Could not switch tenancy", "error"); setSwitchingPast(null); }
    };
    const recTile = (icon, title, sub, onClick, accent) => (
      <button onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: 14, borderRadius: 14, border: `1px solid ${h.bd}`, background: h.cd, cursor: "pointer", textAlign: "left", marginBottom: 10 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: `${accent}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 20 }}>{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: h.tx, fontSize: 14, fontWeight: 700 }}>{title}</div>
          <div style={{ color: h.sc, fontSize: 11, marginTop: 2 }}>{sub}</div>
        </div>
        <span style={{ color: h.tt, fontSize: 18, flexShrink: 0 }}>{"›"}</span>
      </button>
    );
    return <div style={{ paddingTop: 16 }}>
      <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{t.welcome}{me?.name ? `, ${me.name}` : ""} 👋</h2>
      <div style={{ color: h.sc, fontSize: 12, marginBottom: 14 }}>{t.tenancyEndedReadOnly || "Your tenancy has ended — here are your records, read-only."}</div>

      {/* Past-tenancy switcher — only when the tenant vacated 2+ properties.
          Tapping a different one re-scopes the whole read-only portal (records,
          agreement, settlement) to that tenancy via a fresh former token. */}
      {pastTenancies.length > 1 && <div style={{ marginBottom: 12 }}>
        <div style={{ color: h.ts, fontSize: 10, fontWeight: 700, letterSpacing: 0.3, marginBottom: 6 }}>{(t.yourPastTenancies || "YOUR PAST TENANCIES") + ` (${pastTenancies.length})`}</div>
        <div style={{ borderRadius: 12, border: `1px solid ${h.bd}`, background: h.cd, overflow: "hidden" }}>
          {pastTenancies.map((pt, i) => {
            const isCur = pt.tenant_id === ct;
            const busy = switchingPast === pt.tenant_id;
            return <div key={pt.tenant_id} onClick={() => !isCur && !busy && switchPast(pt.tenant_id)} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "11px 13px",
              borderTop: i === 0 ? "none" : `1px solid ${h.bd}`,
              background: isCur ? `${h.ac}08` : "transparent",
              cursor: isCur ? "default" : (busy ? "wait" : "pointer"), opacity: busy ? 0.6 : 1,
            }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: isCur ? `${h.ac}20` : h.ib, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 15 }}>{"🏠"}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: h.tx, fontSize: 13, fontWeight: isCur ? 700 : 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pt.property_addr || pt.org_name || "Past tenancy"}</div>
                <div style={{ color: h.ts, fontSize: 10, marginTop: 1 }}>{(pt.start_date || "—") + " — " + (pt.end_date || "move-out")}</div>
              </div>
              {isCur ? <span style={{ color: h.ac, fontSize: 9, fontWeight: 700, background: `${h.ac}15`, padding: "2px 8px", borderRadius: 10, flexShrink: 0 }}>{t.viewing || "Viewing"}</span>
                : busy ? <span style={{ color: h.ts, fontSize: 10, flexShrink: 0 }}>{"…"}</span>
                : <span style={{ color: h.tt, fontSize: 16, flexShrink: 0 }}>{"›"}</span>}
            </div>;
          })}
        </div>
      </div>}

      <div style={{ ...crd(h), padding: 16, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: pastProp ? 10 : 0 }}>
          <span style={{ background: `${h.ac}15`, color: h.ac, padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700, letterSpacing: 0.3 }}>{t.pastTenancy || "PAST TENANCY"}</span>
        </div>
        {pastProp ? <>
          <div style={{ color: h.tx, fontSize: 14, fontWeight: 600 }}>{formatPropAddr(pastProp)}</div>
          {pastLease && (pastLease.start_date || pastLease.end_date) && <div style={{ color: h.sc, fontSize: 11, marginTop: 3 }}>{(pastLease.start_date || "—") + " — " + (pastLease.end_date || "move-out")}</div>}
        </> : <div style={{ color: h.sc, fontSize: 12 }}>{orgName || "Your previous landlord"}</div>}
        {paidCount > 0 && <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${h.bd}`, display: "flex", gap: 22 }}>
          <div><div style={{ color: h.sc, fontSize: 10 }}>{t.paymentsMade || "Payments made"}</div><div style={{ color: h.tx, fontSize: 16, fontWeight: 700 }}>{paidCount}</div></div>
          <div><div style={{ color: h.sc, fontSize: 10 }}>{t.totalPaid || "Total paid"}</div><div style={{ color: h.tx, fontSize: 16, fontWeight: 700 }}>{"₹" + totalPaid.toLocaleString()}</div></div>
        </div>}
      </div>

      {(landlordRep?.count > 0 || canRateLandlord) && <div style={{ ...crd(h), padding: 12, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: h.sc, fontSize: 11, fontWeight: 600, marginBottom: 3 }}>{"🏠 " + (orgName || "Your landlord")}</div>
          <RatingSummary avg={landlordRep?.avg || 0} count={landlordRep?.count || 0} h={h} size={13} />
        </div>
        {canRateLandlord && <button onClick={() => setRateLandlordOpen(true)} style={{ flexShrink: 0, padding: "8px 14px", borderRadius: 10, border: `1px solid ${h.ac}`, background: "transparent", color: h.ac, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>★ {t.rateLandlord || "Rate landlord"}</button>}
      </div>}
      {rateLandlordOpen && <WriteReviewModal h={h} t={t} flash={flash} subject={{ type: "landlord", orgId, name: orgName || "your landlord" }} onClose={() => setRateLandlordOpen(false)} onDone={() => { setCanRateLandlord(false); api.getLandlordReviews(orgId).then(d => { if (d && d.success) setLandlordRep(d); }).catch(() => {}); }} />}

      {recTile("🧾", t.paymentHistory || "Payment History", t.everyRentPaid || "Every rent & advance you paid", () => go("pay"), h.gn || "#16a34a")}
      {pastLease && recTile("📄", t.rentalAgreement || "Rental Agreement", t.leasePhotos || "Your signed lease + move-in/out photos", () => go("tenLease"), h.ac)}
      {recTile("🗺️", t.browseProperties || "Browse Properties", t.findNextHome || "Find your next home on the map", () => go("mapView"), h.wn || "#d97706")}
      {recTile("📩", t.myInquiries || "My Inquiries", t.myInquiriesSub || "Properties you've sent an inquiry to", () => go("tenInquiries"), h.ac)}

      <div style={{ textAlign: "center", color: h.ts, fontSize: 11, marginTop: 16, lineHeight: 1.6 }}>
        {t.formerTenantFooter || "Need something from your old tenancy? Contact your landlord."}
      </div>
    </div>;
  }

  return <div style={{ paddingTop: 16 }}><h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, marginBottom: (myOrgs.length > 1 || hasAttention) ? 10 : 16 }}>{t.welcome}{me?.name ? `, ${me.name}` : ""}! 👋</h2>

    {/* Active Vacate Request banner — only when a request from the
        new vacate flow is in flight. Status copy is contextual so
        the tenant knows what stage they're at. Tap takes them back
        to the VacatePage to review photos / notes. */}
    {(landlordRep?.count > 0 || canRateLandlord) && <div style={{ ...crd(h), padding: 12, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: h.sc, fontSize: 11, fontWeight: 600, marginBottom: 3 }}>{"🏠 " + (orgName || "Your landlord")}</div>
        <RatingSummary avg={landlordRep?.avg || 0} count={landlordRep?.count || 0} h={h} size={13} />
      </div>
      {canRateLandlord && <button onClick={() => setRateLandlordOpen(true)} style={{ flexShrink: 0, padding: "8px 14px", borderRadius: 10, border: `1px solid ${h.ac}`, background: "transparent", color: h.ac, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>★ Rate landlord</button>}
    </div>}
    {rateLandlordOpen && <WriteReviewModal h={h} t={t} flash={flash} subject={{ type: "landlord", orgId, name: orgName || "your landlord" }} onClose={() => setRateLandlordOpen(false)} onDone={() => { setCanRateLandlord(false); api.getLandlordReviews(orgId).then(d => { if (d && d.success) setLandlordRep(d); }).catch(() => {}); }} />}
    {activeVacate && (() => {
      const s = activeVacate.status;
      const cfg = ({
        initiated:               { bg: h.wn, label: "Vacate request — finish your move-out photos",  cta: "Continue" },
        under_review:            { bg: h.ac, label: "Vacate submitted — awaiting landlord review",   cta: "View" },
        full_refund_proposed:    { bg: h.gn, label: "Full deposit refund proposed — review & accept", cta: "Review" },
        deduction_proposed:      { bg: h.rd, label: "Deductions proposed — review the breakdown",     cta: "Review" },
      })[s] || { bg: h.ac, label: `Vacate request: ${s.replace(/_/g, " ")}`, cta: "View" };
      return (
        <div onClick={() => go("tenVacate")}
          style={{
            marginBottom: 14,
            padding: "12px 14px",
            borderRadius: 12,
            background: `${cfg.bg}10`,
            border: `1px solid ${cfg.bg}40`,
            borderLeft: `3px solid ${cfg.bg}`,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 10,
            transition: "transform 0.12s ease",
          }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: `${cfg.bg}15`,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke={cfg.bg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              aria-hidden="true">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: h.tx, fontSize: 12.5, fontWeight: 700, lineHeight: 1.3 }}>
              {cfg.label}
            </div>
            <div style={{ color: h.sc, fontSize: 10, marginTop: 2 }}>
              Tap to view details
            </div>
          </div>
          <span style={{
            background: cfg.bg, color: "#fff",
            padding: "5px 12px", borderRadius: 999,
            fontSize: 10, fontWeight: 700, letterSpacing: 0.3,
            flexShrink: 0,
          }}>{cfg.cta} →</span>
        </div>
      );
    })()}

    {/* Org Switcher — shown only if tenant has 2+ orgs */}
    {myOrgs.length > 1 && <div style={{ marginBottom: 12 }}>
      <button onClick={() => setShowOrgPicker(!showOrgPicker)} style={{
        display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "10px 14px",
        borderRadius: 12, border: `1px solid ${h.ac}30`, background: `${h.ac}06`, cursor: "pointer",
        transition: "all 0.2s"
      }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: `${h.ac}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Ic n="building" s={16} c={h.ac} />
        </div>
        <div style={{ flex: 1, textAlign: "left" }}>
          <div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{orgName || "Organization"}</div>
          <div style={{ color: h.ts, fontSize: 10 }}>{myOrgs.length} {t.tenancies || "properties"} · {t.tapToSwitch || "Tap to switch"}</div>
        </div>
        <Ic n={showOrgPicker ? "chevron-up" : "chevron-down"} s={16} c={h.tt} />
      </button>

      {showOrgPicker && <div style={{ marginTop: 6, borderRadius: 12, border: `1px solid ${h.bd}`, background: h.cd, overflow: "hidden", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
        {myOrgs.map(org => {
          const isActive = org.org_id === orgId;
          const isSwitching = switchingOrg === org.org_id;
          return <div key={org.tenant_id} onClick={() => !isSwitching && handleSwitchOrg(org)} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "12px 14px",
            cursor: isSwitching ? "wait" : "pointer", borderBottom: `1px solid ${h.bd}`,
            background: isActive ? `${h.ac}08` : "transparent",
            opacity: isSwitching ? 0.6 : 1, transition: "background 0.15s"
          }}>
            <div style={{ width: 28, height: 28, borderRadius: 7, background: isActive ? `${h.ac}20` : h.ib, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Ic n="building" s={14} c={isActive ? h.ac : h.tt} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: h.tx, fontSize: 13, fontWeight: isActive ? 700 : 500 }}>{org.org_name}</div>
              {org.property_addr && <div style={{ color: h.ts, fontSize: 10, marginTop: 1 }}>{org.property_addr}</div>}
            </div>
            {isActive && <span style={{ color: h.ac, fontSize: 10, fontWeight: 700, background: `${h.ac}15`, padding: "2px 8px", borderRadius: 10 }}>{t.current || "Current"}</span>}
            {isSwitching && <span style={{ color: h.ts, fontSize: 10 }}>Switching...</span>}
          </div>;
        })}
      </div>}
    </div>}

    {hasAttention && <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
      {dueIsOverdue && <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: `${h.rd}15`, color: h.rd, padding: "6px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>🔴 {t.rentOverdue}</div>}
      {pendingApproval && !dueIsOverdue && <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: `${h.wn}15`, color: h.wn, padding: "6px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>⏳ {t.payPending}</div>}
      {myLease && isExpiringSoon(myLease) && <div onClick={() => go("tenLease")} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: `${h.wn}15`, color: h.wn, padding: "6px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>📋 {t.leaseExpiringIn} {myLeaseDays} {t.days}</div>}
      {myOpenMaint.length > 0 && <div onClick={() => go("maint")} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: `${h.ac}15`, color: h.ac, padding: "6px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>🔧 {t.maintUpdate} ({myOpenMaint.length})</div>}
      {hasDraftLease && <div onClick={() => go("tenLease")} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: `${h.wn}15`, color: h.wn, padding: "6px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>📋 {t.agreementReady || "Agreement Ready — Sign Now"}</div>}
    </div>}
    {/* ── Pending Association Requests ─────────────── */}
    {pendingRequests.length > 0 && pendingRequests.map(req => (
      <div key={req.id} style={{ ...crd(h), padding: 16, marginBottom: 12, border: `2px solid ${h.ac}`, background: `${h.ac}05` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: 20, background: `${h.ac}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 20 }}>🏠</span>
          </div>
          <div>
            <div style={{ color: h.ac, fontWeight: 700, fontSize: 14 }}>Property Association Request</div>
            <div style={{ color: h.sc, fontSize: 11 }}>from {req.org_name || "Landlord"}</div>
          </div>
        </div>
        {req.property_addr && <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <Ic n="map-pin" s={12} c={h.sc} />
          <span style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{req.property_addr}</span>
        </div>}
        {req.rent_amount > 0 && <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <span style={{ color: h.sc, fontSize: 12 }}>Rent:</span>
          <span style={{ color: h.tx, fontWeight: 700, fontSize: 14 }}>₹{req.rent_amount.toLocaleString()}/mo</span>
          <span style={{ color: h.sc, fontSize: 11 }}>· Due: {req.rent_due_day || 1}{["st","nd","rd"][((req.rent_due_day||1)-1)%10>2?3:(req.rent_due_day||1)-1]||"th"} of month</span>
        </div>}
        {req.message && <div style={{ color: h.sc, fontSize: 11, fontStyle: "italic", marginBottom: 8 }}>"{req.message}"</div>}
        <div style={{ background: `${h.wn}08`, borderRadius: 8, padding: 8, marginBottom: 10 }}>
          <div style={{ color: h.wn, fontSize: 10, fontWeight: 600 }}>⚠️ Your profile (name, phone, ID) will be shared with this landlord</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => handleRespond(req.id, "approve")} disabled={respondingId === req.id}
            style={{ flex: 1, padding: 10, borderRadius: 10, border: "none", background: h.gn, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
            {respondingId === req.id ? "..." : "✓ Approve & Join"}
          </button>
          <button onClick={() => handleRespond(req.id, "decline")} disabled={respondingId === req.id}
            style={{ flex: 1, padding: 10, borderRadius: 10, border: `1px solid ${h.rd}`, background: "transparent", color: h.rd, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
            ✕ Decline
          </button>
        </div>
      </div>
    ))}

    {/* ── Sign Agreement Banner ─────────────── */}
    {hasDraftLease && myProp && <div onClick={() => go("tenLease")} style={{ ...crd(h), padding: 16, marginBottom: 12, border: `2px solid ${h.wn}`, background: `${h.wn}06`, cursor: "pointer" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{ width: 44, height: 44, borderRadius: 22, background: `${h.wn}15`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <span style={{ fontSize: 22 }}>📋</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: h.wn, fontWeight: 700, fontSize: 15 }}>{t.signAgreement || "Sign Your Rental Agreement"}</div>
          <div style={{ color: h.sc, fontSize: 11, marginTop: 2 }}>{t.landlordCreatedAgreement || "Your landlord has created a rental agreement for you to review and sign."}</div>
        </div>
      </div>
      <div style={{ background: `${h.wn}10`, borderRadius: 10, padding: 10, marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <Ic n="map-pin" s={12} c={h.sc} />
          <span style={{ color: h.tx, fontSize: 12, fontWeight: 600 }}>{formatPropAddr(myProp)}</span>
        </div>
        {myLease && <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {myLease.start_date && <span style={{ color: h.sc, fontSize: 11 }}>From: {myLease.start_date}</span>}
          {myLease.rent && <span style={{ color: h.sc, fontSize: 11 }}>Rent: ₹{Number(myLease.rent).toLocaleString()}/mo</span>}
        </div>}
      </div>
      <button style={{ width: "100%", padding: 12, borderRadius: 10, border: "none", background: h.wn, color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
        ✍️ {t.reviewAndSign || "Review & Sign Agreement"}
      </button>
    </div>}

    {!myProp && myUnits.length === 0 && <div style={{ ...crd(h), textAlign: "center", padding: 24 }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>{"\uD83C\uDFE0"}</div>
      <div style={{ color: h.sc, fontSize: 14 }}>{t.noProperty}</div>
      <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>{t.noPropChat}</div>
      <button onClick={() => go("mapView")} style={{ marginTop: 12, padding: "10px 20px", borderRadius: 10, border: "none", background: h.ac, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
        {"\uD83D\uDDFA\uFE0F"} {t.browseProperties || "Browse Properties"}
      </button>
    </div>}

    {/* Multi-unit view: show all assigned units */}
    {myUnits.length > 1 && <div style={{ marginBottom: 12 }}>
      <div style={{ color: h.ac, fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{t.myUnits || "My Units"} ({myUnits.length})</div>
      {myUnits.map(unit => {
        const unitProp = props.find(p => p.id === unit.property_id);
        return <div key={unit.id} style={{ ...crd(h), marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>{unit.unit_label || "Unit"}</div>
              <div style={{ color: h.sc, fontSize: 11, marginTop: 2 }}>{formatPropAddr(unitProp)}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: h.tx, fontWeight: 700, fontSize: 14 }}>{"\u20B9"}{(unit.rent || 0).toLocaleString()}</div>
              <div style={{ color: h.sc, fontSize: 10 }}>/mo</div>
            </div>
          </div>
          {unit.rooms && <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>{unit.rooms} {t.rooms}{unit.area_sqft ? ` · ${unit.area_sqft} sqft` : ""}</div>}
        </div>;
      })}
    </div>}

    {/* Single property view (backward compatible) */}
    {myProp && myUnits.length <= 1 && <div style={crd(h)}><div style={{ color: h.ac, fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{(() => {
      const pt = myProp.property_type;
      const labels = { apartment: t.myApartment || "My Apartment", independent_house: t.myHouse || "My House", villa: t.myVilla || "My Villa", pg_room: t.myPgRoom || "My PG Room", office: t.myOffice || "My Office", shop: t.myShop || "My Shop", showroom: t.myShowroom || "My Showroom", warehouse: t.myWarehouse || "My Warehouse", clinic: t.myClinic || "My Clinic", coworking: t.myCoworking || "My Coworking Space", industrial: t.myIndustrial || "My Industrial Space" };
      return labels[pt] || t.myHouse || "My Property";
    })()}</div>
      <div style={{ color: h.tx, fontSize: 13 }}>{formatPropAddr(myProp)}</div><div style={{ color: h.sc, fontSize: 11 }}>{myProp.rooms} {t.rooms} · {"\u20B9"}{myProp.rent.toLocaleString()}/mo</div>
      <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>{(myProp.amen || []).map(a => <span key={a} style={{ background: `${h.ac}12`, color: h.ac, padding: "2px 8px", borderRadius: 10, fontSize: 10 }}>{a}</span>)}</div>
      {(() => {
        const fit = getPropertyFit(myProp, { leases, role, t, h });
        if (!fit) return null;
        if (!showFit) {
          // Collapsed: compact score pill + "tap to see why" hint.
          return <button onClick={() => setShowFit(true)} style={{
            marginTop: 8, width: "100%",
            display: "flex", alignItems: "center", gap: 8,
            padding: "8px 10px", borderRadius: 10,
            background: `${fit.color}08`, border: `1px solid ${fit.color}25`,
            cursor: "pointer", textAlign: "left",
          }} aria-expanded="false">
            <FitBadge fit={fit} h={h} size="sm" />
            <span style={{ flex: 1, color: h.sc, fontSize: 11 }}>{t.whyMatches || "Why this matches"}</span>
            <span style={{ color: fit.color, fontSize: 14, flexShrink: 0 }}>▾</span>
          </button>;
        }
        // Expanded: full FitBadge + inline tag chips. Tap anywhere on
        // this block to collapse — symmetrical with the collapsed pill
        // above which taps to expand. Up-chevron in the top-right
        // gives the affordance without a dedicated button.
        return <div
          onClick={() => setShowFit(false)}
          role="button"
          aria-expanded="true"
          style={{
            marginTop: 8, position: "relative",
            cursor: "pointer", paddingRight: 18,
          }}>
          <span style={{
            position: "absolute", top: 0, right: 0,
            color: fit.color, fontSize: 14, lineHeight: 1,
          }} aria-hidden="true">▴</span>
          <FitBadge fit={fit} h={h} size="md" />
          <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
            {fit.tags.slice(0, 3).map((tag, i) => <span key={i} style={{ background: `${fit.color}08`, color: fit.color, padding: "1px 7px", borderRadius: 8, fontSize: 9 }}>{tag}</span>)}
          </div>
        </div>;
      })()}
      {myProp.latitude && myProp.longitude && <button onClick={() => go("mapView")} style={{ marginTop: 10, width: "100%", padding: 10, borderRadius: 10, border: `1px solid ${h.ac}`, background: `${h.ac}10`, color: h.ac, cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>{"\uD83D\uDDFA " + (t.mapView || "Map View")}</button>}
      {myLease && <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${h.bd}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ color: h.sc, fontSize: 11 }}>{t.lease}</span>
          <span style={bdg(`${leaseStatusColor(myLeaseStatus, h)}20`, leaseStatusColor(myLeaseStatus, h))}>{leaseStatusLabel(myLeaseStatus, t)}</span>
        </div>
        <div style={{ color: h.sc, fontSize: 11 }}>{myLease.start_date} — {myLease.end_date || "—"}</div>
        {isExpiringSoon(myLease) && <div style={{ color: h.wn, fontSize: 11, fontWeight: 600, marginTop: 4 }}>⚠️ {myLeaseDays} {t.daysRemaining}</div>}
        <button onClick={() => go("tenLease")} style={{ marginTop: 8, width: "100%", padding: 8, borderRadius: 8, border: `1px solid ${h.ac}`, background: `${h.ac}10`, color: h.ac, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>{t.viewLease}</button>
      </div>}
      <div style={{ marginTop: 12, borderTop: `1px solid ${h.bd}`, paddingTop: 12 }}>
        {/* Vacate-request CTA — three states, ordered by precedence:
              1. Legacy me.vac=1 (old request endpoint, awaiting
                 landlord approve/reject) — keep the orange
                 'requesting' chip + continue link.
              2. activeVacate present (new state-machine flow) —
                 swap the red 'Vacate Request' button to a blue
                 'View Vacate Request' that mirrors View Lease.
                 The destructive-action styling is wrong once a
                 request exists; this is informational now.
              3. No active vacate at all — show the original red
                 destructive-action button to START a request. */}
        {me.vac ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ background: `${h.wn}15`, color: h.wn, padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, textAlign: "center" }}>{"⏳ " + t.vacRequesting}</div>
            <button onClick={() => go("tenVacate")} style={{ width: "100%", padding: "8px", borderRadius: 8, border: `1px solid ${h.ac}`, background: `${h.ac}10`, color: h.ac, cursor: "pointer", fontSize: 11, fontWeight: 600 }}>Continue Vacate Process →</button>
          </div>
        ) : activeVacate ? (
          <button onClick={() => go("tenVacate")}
            style={{
              width: "100%", padding: "10px", borderRadius: 10,
              border: `1px solid ${h.ac}`,
              background: `${h.ac}10`,
              color: h.ac,
              cursor: "pointer", fontSize: 12, fontWeight: 600,
            }}>
            {t.viewVacateRequest || "View Vacate Request"}
          </button>
        ) : (
          <button onClick={() => go("tenVacate")}
            style={{
              width: "100%", padding: "10px", borderRadius: 10,
              border: `1px solid ${h.rd}`,
              background: "transparent",
              color: h.rd,
              cursor: "pointer", fontSize: 12, fontWeight: 600,
            }}>
            {t.vacate}
          </button>
        )}
      </div>
    </div>}

    {duePay && <div style={{ ...crd(h), padding: 16, border: `2px solid ${pendingApproval ? h.wn : h.rd}` }}>
      {pendingApproval ? <>
        <div style={{ color: h.wn, fontWeight: 700, fontSize: 14, marginBottom: 10 }}>{"⏳ " + t.payWaiting}</div>
        <div style={{ background: `${h.wn}10`, borderRadius: 12, padding: 14, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: h.sc, fontSize: 12 }}>{t.amtSubmitted}</span><span style={{ color: h.tx, fontWeight: 700, fontSize: 14 }}>₹{pendingApproval.amt.toLocaleString()}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: h.sc, fontSize: 12 }}>{t.method}</span><span style={{ color: h.tx, fontWeight: 600, fontSize: 12 }}>{pendingApproval.mth.toUpperCase()}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: h.sc, fontSize: 12 }}>{t.date}</span><span style={{ color: h.tx, fontWeight: 500, fontSize: 12 }}>{pendingApproval.dt}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: h.sc, fontSize: 12 }}>{t.status}</span><span style={bdg(`${h.wn}20`, h.wn)}>{"⏳ " + t.waitApproval}</span></div>
        </div>
        {pendingApproval.amt < totalDue && <div style={{ background: `${h.ac}10`, borderRadius: 10, padding: 12, marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span style={{ color: h.sc, fontSize: 12 }}>{t.totalDue}</span><span style={{ color: h.tx, fontSize: 12 }}>₹{totalDue.toLocaleString()}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: h.sc, fontSize: 12 }}>{t.willRemain}</span><span style={{ color: h.rd, fontWeight: 700, fontSize: 12 }}>₹{(totalDue - pendingApproval.amt).toLocaleString()}</span></div>
        </div>}
        <div style={{ textAlign: "center", color: h.sc, fontSize: 11, marginTop: 8 }}>{t.payVerifyMsg}</div>
      </> : <>
        <div style={{ color: h.rd, fontWeight: 700, fontSize: 14, marginBottom: 10 }}>{dueIsOverdue ? "🔴 " + t.overdueRent + " — " + duePay.mo : duePay.st === "partial" ? "🟡 Partial Paid — " + duePay.mo : "⚠️ " + t.payDue}</div>
        {alreadyPaid > 0 && <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, padding: "6px 10px", background: "rgba(22,163,74,0.08)", borderRadius: 8 }}>
          <span style={{ color: "#16a34a", fontSize: 12, fontWeight: 600 }}>✅ Paid: ₹{alreadyPaid.toLocaleString()}</span>
          <span style={{ color: h.sc, fontSize: 11 }}>Remaining: ₹{totalDue.toLocaleString()}</span>
        </div>}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
          <div style={{ background: `${h.rd}10`, borderRadius: 10, padding: 10, textAlign: "center" }}><div style={{ color: h.sc, fontSize: 10 }}>{t.rentAmt}</div><div style={{ color: h.tx, fontWeight: 700, fontSize: 16 }}>₹{duePay.amt.toLocaleString()}</div></div>
          {(() => { const comTotal = (duePay.com || 0) + maintComTotal; const hasMaint = maintComTotal > 0; return <>
            <div onClick={() => setShowCommonSplit(true)} style={{ background: `${h.ac}10`, borderRadius: 10, padding: 10, textAlign: "center", cursor: "pointer" }}><div style={{ color: h.sc, fontSize: 10 }}>{hasMaint ? (t.commonAndMaint || "Common & Maint") : t.commonAmt} ▸</div><div style={{ color: h.ac, fontWeight: 700, fontSize: 16, textDecoration: "underline" }}>₹{comTotal.toLocaleString()}</div></div>
            <div style={{ background: `${h.wn}10`, borderRadius: 10, padding: 10, textAlign: "center" }}><div style={{ color: h.sc, fontSize: 10 }}>{t.totalAmt}</div><div style={{ color: h.wn, fontWeight: 700, fontSize: 16 }}>₹{netGrandDue.toLocaleString()}</div></div>
          </>; })()}
        </div>

        {/* Advance auto-applied chip — surfaces the netting that just
            reduced the Total tile. Without this, a tenant with a ₹1,000
            advance who suddenly sees ₹9,000 instead of ₹10,000 would
            wonder where the missing ₹1,000 went. */}
        {appliedAdvance > 0 && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 6, marginBottom: 10, padding: "8px 12px",
            background: `${h.gn || "#16a34a"}0d`,
            borderRadius: 10,
            border: `1px solid ${h.gn || "#16a34a"}30`,
          }}>
            <span style={{ display: "flex", alignItems: "center", gap: 6, color: h.gn || "#16a34a", fontSize: 12, fontWeight: 600 }}>
              💰 {t.advanceApplied || "Advance applied"}
            </span>
            <span style={{ color: h.gn || "#16a34a", fontWeight: 700, fontSize: 13 }}>−₹{appliedAdvance.toLocaleString()}</span>
          </div>
        )}

        {awaitingApproval > 0 && <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 10, padding: "6px 10px", background: `${h.wn}10`, borderRadius: 8, border: `1px solid ${h.wn}25` }}>
          <span style={{ color: h.wn, fontSize: 11, fontWeight: 600 }}>⏳ ₹{awaitingApproval.toLocaleString()} awaiting landlord approval</span>
        </div>}
        {!showPay ? <button onClick={() => { materializeAdvance().finally(() => go("tenPay")); }} style={{ ...btnP(h), marginTop: 0, background: h.ac }}>{t.payNow} ₹{netGrandDue.toLocaleString()}</button>
          : <div style={{ background: `${h.ac}05`, borderRadius: 12, padding: 16, marginTop: 4 }}>
            <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 6 }}>{t.method}</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>{["upi", "cash", "bank"].map(m => <button key={m} onClick={() => setPayMth(m)} style={{ flex: 1, padding: 10, borderRadius: 10, border: `1px solid ${payMth === m ? h.ac : h.bd}`, background: payMth === m ? `${h.ac}15` : "transparent", color: payMth === m ? h.ac : h.sc, cursor: "pointer", fontSize: 12, fontWeight: 500 }}>{m === "upi" ? t.upi : m === "cash" ? t.cash : t.bank}</button>)}</div>

            {payMth === "upi" && <div style={{ background: h.cd, borderRadius: 12, padding: 16, textAlign: "center", marginBottom: 12, border: `1px solid ${h.bd}` }}>
              {settings?.upi_id ? <>
                {(() => { const upiAmt = Number(payAmt) > 0 ? Number(payAmt) : totalDue; const upiUrl = `upi://pay?pa=${settings.upi_id.replace(/\s/g, '')}&pn=${encodeURIComponent((settings?.upi_name || "Owner").trim())}&am=${upiAmt}&cu=INR&tn=${encodeURIComponent(`Rent - ${me?.name || "Tenant"}`)}`; return <>
                  <div style={{ color: h.ac, fontSize: 11, fontWeight: 600, marginBottom: 8 }}>{!upiOpened ? t.upiStep1 : t.upiStep2}</div>
                  <div style={{ background: "white", padding: 10, borderRadius: 12, margin: "0 auto 10px", width: "fit-content" }}>
                    <QRCode value={upiUrl} size={128} />
                  </div>
                  {!upiOpened ? <a href={upiUrl}
                    onClick={(e) => { if (!(Number(payAmt) > 0)) { e.preventDefault(); flash(t.fillRequired, "error"); return; } setUpiOpened(true); }}
                    style={{ background: h.ac, color: "#fff", fontSize: 13, fontWeight: 600, textDecoration: "none", display: "block", marginTop: 8, padding: "10px 16px", borderRadius: 10, textAlign: "center" }}>
                    {t.openUpiApp} — ₹{upiAmt.toLocaleString()}
                  </a>
                  : <button onClick={() => { handlePay(); setUpiOpened(false); }}
                    style={{ width: "100%", background: h.gn, color: "#fff", fontSize: 13, fontWeight: 600, border: "none", marginTop: 8, padding: "12px 16px", borderRadius: 10, cursor: "pointer" }}>
                    {"✓ " + t.iHavePaid}
                  </button>}
                </>; })()}
                <div style={{ color: h.sc, fontSize: 11, marginTop: 6 }}>{t.tapUpi}</div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 6 }}>
                  <span style={{ color: h.sc, fontSize: 10 }}>UPI ID:</span>
                  <span style={{ color: h.tx, fontSize: 11, fontWeight: 500 }}>{settings?.upi_id}</span>
                  <CopyBtn value={settings?.upi_id} label="UPI ID" h={h} />
                </div>
              </> : <div style={{ color: h.rd, fontSize: 12, padding: 10 }}>{t.upiNotLoaded}</div>}
            </div>}

            {payMth === "bank" && <div style={{ background: h.cd, borderRadius: 12, padding: 16, marginBottom: 12, border: `1px solid ${h.bd}` }}>
              <div style={{ color: h.tx, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{t.bankTransferDetails}</div>
              {(() => {
                // getBankRows() prefers structured fields, but falls back to
                // parsing the legacy single-blob `bank_details` so the tenant
                // always gets per-field copy buttons even for landlords who
                // never re-saved settings after the structured-fields migration.
                const rows = getBankRows(settings);
                if (rows.length === 0) {
                  return <div style={{ color: h.sc, fontSize: 12 }}>Not configured</div>;
                }
                return rows.map(r => (
                  <div key={r.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px dashed ${h.bd}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: h.sc, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>{r.label}</div>
                      <div style={{ color: h.tx, fontSize: 13, fontWeight: 500, wordBreak: "break-all" }}>{r.value}</div>
                    </div>
                    <CopyBtn value={r.value} label={r.label} h={h} />
                  </div>
                ));
              })()}
            </div>}

            <label style={{ color: h.sc, fontSize: 12, fontWeight: 500, display: "block", marginBottom: 4 }}>{t.amt}</label>
            <input value={payAmt} onChange={e => setPayAmt(e.target.value)} type="number" placeholder={`Total: ₹${totalDue}`} style={inpS(h)} />

            {payAmt && Number(payAmt) > 0 && Number(payAmt) < totalDue && <div style={{ background: `${h.wn}15`, borderRadius: 10, padding: 12, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span style={{ color: h.sc, fontSize: 12 }}>{t.payingNow}</span><span style={{ color: h.gn, fontSize: 12, fontWeight: 700 }}>₹{Number(payAmt).toLocaleString()}</span></div>
              <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: h.sc, fontSize: 12 }}>{t.remainAfterApproval}</span><span style={{ color: h.rd, fontSize: 12, fontWeight: 700 }}>₹{(totalDue - Number(payAmt)).toLocaleString()}</span></div>
              <div style={{ color: h.wn, fontSize: 11, marginTop: 6 }}>{"⚠️ " + t.partialPayWarn}</div>
            </div>}

            {payAmt && Number(payAmt) >= totalDue && <div style={{ background: `${h.gn}15`, borderRadius: 10, padding: 12, marginBottom: 12 }}>
              <div style={{ color: h.gn, fontWeight: 600, fontSize: 13 }}>{"✓ " + t.fullPayMsg + " — ₹" + totalDue.toLocaleString()}</div>
              <div style={{ color: h.sc, fontSize: 11, marginTop: 4 }}>{t.clearAfterApproval}</div>
            </div>}

            <div style={{ display: "flex", gap: 10 }}>
              {payMth !== "upi" && <button onClick={handlePay} style={{ ...btnP(h), flex: 1, marginTop: 0 }}>{t.submitPay + " ₹" + (payAmt || 0)}</button>}
              <button onClick={() => { setShowPay(false); setPayAmt(""); }} style={{ ...btnSec(h), flex: 1, marginTop: 0 }}>{t.cancel}</button>
            </div>
            <div style={{ color: h.sc, fontSize: 10, textAlign: "center", marginTop: 8 }}>💡 Payment will reflect only after landlord verifies and approves</div>
          </div>}
      </>}
    </div>}

    {/* No rent due — check if maintenance charges exist */}
    {!duePay && (() => {
      const maintDue = myPay.filter(x => (x.st === "due" || x.st === "partial") && (x.mo || "").startsWith("🔧"));
      // Net out amounts already submitted and awaiting landlord approval —
      // otherwise this card asks the tenant to pay the same charge twice
      // (and TenPayNow would record nothing for the duplicate).
      const maintPending = maintDue.reduce((s, p) => s + (p.pending?.amt || 0), 0);
      const maintTotal = maintDue.reduce((s, p) => s + Math.max(0, (p.amt || 0) + (p.com || 0) - (p.pending?.amt || 0)), 0);
      if (maintTotal > 0) return <div style={{ ...crd(h), padding: 16, border: `2px solid ${h.wn}` }}>
        <div style={{ color: h.wn, fontWeight: 700, fontSize: 14, marginBottom: 10 }}>🔧 {t.maintenanceCharges || "Maintenance Charges"}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
          <div style={{ background: `${h.rd}10`, borderRadius: 10, padding: 10, textAlign: "center" }}><div style={{ color: h.sc, fontSize: 10 }}>{t.rentAmt}</div><div style={{ color: h.tx, fontWeight: 700, fontSize: 16 }}>₹0</div></div>
          <div onClick={() => setShowCommonSplit(true)} style={{ background: `${h.ac}10`, borderRadius: 10, padding: 10, textAlign: "center", cursor: "pointer" }}><div style={{ color: h.sc, fontSize: 10 }}>{t.commonAndMaint || "Common & Maint"} ▸</div><div style={{ color: h.ac, fontWeight: 700, fontSize: 16, textDecoration: "underline" }}>₹{maintTotal.toLocaleString()}</div></div>
          <div style={{ background: `${h.wn}10`, borderRadius: 10, padding: 10, textAlign: "center" }}><div style={{ color: h.sc, fontSize: 10 }}>{t.totalAmt}</div><div style={{ color: h.wn, fontWeight: 700, fontSize: 16 }}>₹{maintTotal.toLocaleString()}</div></div>
        </div>
        <button onClick={() => go("tenPay")} style={{ width: "100%", padding: 14, borderRadius: 12, border: "none", background: h.ac, color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>{t.payNow} ₹{maintTotal.toLocaleString()}</button>
      </div>;
      if (maintPending > 0) return <div style={{ ...crd(h), padding: 16, border: `2px solid ${h.wn}` }}>
        <div style={{ color: h.wn, fontWeight: 700, fontSize: 14 }}>⏳ {t.payPending || "Payment Pending Approval"}</div>
        <div style={{ color: h.sc, fontSize: 12, marginTop: 6 }}>₹{maintPending.toLocaleString()} {t.awaitApproval || "awaiting landlord approval"}</div>
      </div>;
      return <div style={{ ...crd(h), textAlign: "center", padding: 20 }}>
        <div style={{ color: h.gn, fontWeight: 600, fontSize: 14 }}>{"✓ " + t.allCleared}</div>
        <div style={{ color: h.sc, fontSize: 12, marginTop: 4 }}>{t.noPendingDues}</div>
        <button onClick={() => go("tenPay")} style={{ marginTop: 12, padding: "10px 20px", borderRadius: 10, border: `1px solid ${h.ac}`, background: `${h.ac}10`, color: h.ac, cursor: "pointer", fontSize: 13, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>{"💰"} Pay Advance</button>
      </div>;
    })()}

    <div style={{ display: "grid", gridTemplateColumns: myProp && myShare > 0 ? "1fr 1fr" : "1fr", gap: 10, margin: "16px 0" }}>
      <MC ic="dollar" l={t.due} v={duePay ? `₹${totalDue.toLocaleString()}` : "₹0"} cl={totalDue > 0 ? h.rd : h.gn} grad={totalDue > 0 ? ["#fb7185", "#e11d48"] : ["#34d399", "#059669"]} h={h} />
      {myProp && myShare > 0 && <div onClick={() => setShowCommonSplit(true)} style={{ cursor: "pointer" }}>
        <MC ic="zap" l={t.common} v={`₹${myShare.toLocaleString()}`} cl={h.ac} grad={["#60a5fa", "#4f46e5"]} h={h} />
      </div>}
    </div>

    {showCommonSplit && (() => {
      const cc = ccs.length > 0 ? ccs[0] : null;
      const hasItems = cc && cc.items && cc.items.length > 0;
      const ccTotal = cc ? (hasItems ? cc.items.reduce((s, it) => s + Number(it.amount || 0), 0) : (cc.eb + cc.cl)) : 0;
      return <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: h.bg, borderRadius: 20, width: "100%", maxWidth: 360, padding: 24, boxShadow: "0 10px 40px rgba(0,0,0,0.2)", position: "relative" }}>
        <button onClick={() => setShowCommonSplit(false)} style={{ position: "absolute", top: 16, right: 16, background: `${h.bd}50`, border: "none", width: 30, height: 30, borderRadius: 15, color: h.tx, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div style={{ width: 50, height: 50, borderRadius: 25, background: `${h.ac}15`, color: h.ac, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px" }}><Ic n="zap" s={24} c={h.ac} /></div>
          <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: 0 }}>{myPay.some(x => (x.st === "due" || x.st === "partial") && (x.mo || "").startsWith("🔧")) ? (t.commonAndMaint || "Common & Maintenance") : t.common}</h2>
          <div style={{ color: h.sc, fontSize: 12, marginTop: 4 }}>{cc ? (t.breakdownFor + " " + cc.mo) : t.noChargesYet}</div>
        </div>

        {cc ? <div style={{ background: `${h.ac}05`, borderRadius: 12, padding: 16, border: `1px solid ${h.bd}` }}>
          {hasItems ? cc.items.map((it, idx) => (
            <div key={idx} style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, paddingBottom: idx < cc.items.length - 1 ? 0 : 12, borderBottom: idx === cc.items.length - 1 ? `1px dashed ${h.bd}` : "none" }}><span style={{ color: h.sc, fontSize: 13 }}>{it.name}</span><span style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>₹{Number(it.amount || 0).toLocaleString()}</span></div>
          )) : <>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}><span style={{ color: h.sc, fontSize: 13 }}>{t.electricity}</span><span style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>₹{cc.eb.toLocaleString()}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, paddingBottom: 12, borderBottom: `1px dashed ${h.bd}` }}><span style={{ color: h.sc, fontSize: 13 }}>{t.cleaningTotal}</span><span style={{ color: h.tx, fontWeight: 600, fontSize: 13 }}>₹{cc.cl.toLocaleString()}</span></div>
          </>}

          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}><span style={{ color: h.sc, fontSize: 12, fontWeight: 500 }}>{t.totalBldgBill}</span><span style={{ color: h.tx, fontWeight: 700, fontSize: 12 }}>₹{ccTotal.toLocaleString()}</span></div>
          <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: h.ac, fontSize: 12, fontWeight: 600 }}>{t.dividedBy + " " + cc.n + " " + t.active}</span><span style={{ color: h.ac, fontWeight: 700, fontSize: 16 }}>₹{myShare.toLocaleString()}</span></div>
          {myMaintCharges.length > 0 && <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px dashed ${h.bd}` }}>
            <div style={{ color: h.wn, fontSize: 11, fontWeight: 600, marginBottom: 8 }}>🛠️ {t.perTenantCharges}</div>
            {myMaintCharges.map((mc, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}><span style={{ color: h.sc, fontSize: 12 }}>{mc.desc}</span><span style={{ display: "inline-flex", alignItems: "center" }}><span style={{ color: maintChargeSt(mc) === "paid" ? "#16a34a" : h.wn, fontWeight: 600, fontSize: 12 }}>₹{mc.exp_amt.toLocaleString()}</span>{maintChargePill(mc)}</span></div>
            ))}
          </div>}
        </div> : <div style={{ background: `${h.ac}05`, borderRadius: 12, padding: 20, border: `1px solid ${h.bd}`, textAlign: "center" }}>
          <div style={{ color: h.sc, fontSize: 13, marginBottom: 8 }}>{t.noChargesMsg}</div>
          <div style={{ color: h.sc, fontSize: 11 }}>{t.chargesWillUpdate}</div>
        </div>}

        {/* Maintenance charges from payments table */}
        {(() => { const maintDue = myPay.filter(x => (x.st === "due" || x.st === "partial") && (x.mo || "").startsWith("🔧")); return maintDue.length > 0 ? <div style={{ background: `${h.wn}08`, borderRadius: 12, padding: 16, border: `1px solid ${h.wn}30`, marginTop: 12 }}>
          <div style={{ color: h.wn, fontSize: 11, fontWeight: 600, marginBottom: 8 }}>🔧 {t.maintenanceCharges || "Maintenance Charges"}</div>
          {maintDue.map((mp, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: h.sc, fontSize: 12 }}>{(mp.mo || "").replace("🔧 ", "")}</span><span style={{ color: h.wn, fontWeight: 600, fontSize: 12 }}>₹{(mp.com || mp.amt || 0).toLocaleString()}</span></div>
          ))}
        </div> : (!cc && myMaintCharges.length > 0 ? <div style={{ background: `${h.wn}08`, borderRadius: 12, padding: 16, border: `1px solid ${h.wn}30`, marginTop: 12 }}>
          <div style={{ color: h.wn, fontSize: 11, fontWeight: 600, marginBottom: 8 }}>🛠️ {t.perTenantCharges}</div>
          {myMaintCharges.map((mc, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}><span style={{ color: h.sc, fontSize: 12 }}>{mc.desc}</span><span style={{ display: "inline-flex", alignItems: "center" }}><span style={{ color: maintChargeSt(mc) === "paid" ? "#16a34a" : h.wn, fontWeight: 600, fontSize: 12 }}>₹{mc.exp_amt.toLocaleString()}</span>{maintChargePill(mc)}</span></div>
          ))}
        </div> : null); })()}
      </div>
    </div>;
    })()}

    {receiptPay && <div style={{ ...crd(h), padding: 0 }}><ReceiptView pay={receiptPay} prop={myProp} tenant={me} onBack={() => setReceiptPay(null)} /></div>}

    {!receiptPay && <><h3 style={{ color: h.tx, fontSize: 14, fontWeight: 700, marginBottom: 10 }}>{t.recentPay}</h3>
    {myPay.slice().sort((a, b) => monthSortValue(b.mo) - monthSortValue(a.mo)).slice(0, 5).map(p => { const pOverdue = p.st === "due" && !p.pending && isOverdue(p.mo);
      const gross = (p.amt || 0) + (p.com || 0);
      const isAdvanceOnly = gross === 0 && (p.advance || 0) > 0;
      const displayAmt = isAdvanceOnly ? p.advance : gross;
      const tone = isAdvanceOnly ? "indigo" : p.st === "paid" ? "green" : p.pending ? "amber" : "red";
      return <div key={p.id}
        onMouseOver={(e) => gradLift(e, tone, true)}
        onMouseOut={(e) => gradLift(e, tone, false)}
        style={{ ...crd(h), ...gradCardStyle(tone), display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div><div style={{ color: "#fff", fontSize: 13, fontWeight: 600 }}>{isAdvanceOnly ? ("💰 " + (t.advancePay || "Advance")) : p.mo}</div><div style={{ color: "rgba(255,255,255,0.85)", fontSize: 11 }}>{p.st === "paid" && p.dt ? `${t.paidOn || "Paid on"} ${p.dt}${p.mth ? " · " + p.mth.toUpperCase() : ""}` : `${p.mth || "—"} · ${p.dt || "—"}`}</div>
        {/* Which stay this payment covers (arrears cycle) — mirrors the
            landlord PayPage card so both sides read the same period. */}
        {rentPeriod(p.mo, me?.rent_due_day) && <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 10, marginTop: 1, display: "flex", alignItems: "center", gap: 3 }}><span>{"🗓️"}</span>{rentPeriod(p.mo, me?.rent_due_day)} {t.rentAmt || "Rent"}</div>}
      </div>
      <div style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}><div style={{ color: "#fff", fontWeight: 700 }}>₹{Number(displayAmt).toLocaleString()}</div>
        <span style={glassPill}>{isAdvanceOnly ? ("💰 " + (t.advance || "Advance")) : p.st === "paid" ? "✓ " + t.paid : p.pending ? "⏳ " + t.pending : pOverdue ? "🔴 " + t.overdue : t.due}</span>
        {p.st === "paid" && <button onClick={() => setReceiptPay(p)} style={{ background: "rgba(255,255,255,0.92)", border: "none", color: h.ac, padding: "3px 10px", borderRadius: 8, cursor: "pointer", fontSize: 10, fontWeight: 600 }}>{"📄 " + t.viewReceipt}</button>}
      </div></div>; })}</>}
  </div>;
}

export default TenDash;
